/**
 * Cấu hình dùng chung cho trang "Order tuyển nhân sự" (recruitment-orders).
 *
 * - Teamlead bất kỳ phòng ban nào (account_type_id = 16 - TEAMLEADER) có thể tạo order
 *   yêu cầu tuyển dụng nhân sự mới.
 * - Order LUÔN phải được duyệt trước khi đi tiếp. Số cấp duyệt phụ thuộc loại order:
 *     + Order phòng ban  : 1 cấp — HR Lead (chị Ngà) duyệt.
 *     + Order Giáo viên  : 2 cấp SONG SONG — chị Vân Anh (chuyên trách TCGV) và chị Ngà
 *       duyệt cùng lúc; chỉ cần 1 trong 2 người từ chối là order bị từ chối.
 * - Thời điểm xác định Teamlead phụ trách cũng khác nhau:
 *     + Order phòng ban  : chọn ngay trong form tạo order.
 *     + Order Giáo viên  : sau khi duyệt xong, người điều phối (chị Cẩm Tú) mới phân công.
 * - Teamlead phụ trách (thuộc phòng Nhân sự / TCGV) tìm CV, nộp thông tin ứng viên.
 * - Teamlead phòng ban gốc (người tạo order) Duyệt/Từ chối CV được đề xuất. Nếu từ chối,
 *   order quay lại "Đang tìm CV" (không đóng order) và lưu lại lịch sử CV đã đề xuất.
 */
import {
    TEAM_LEAD_ACCOUNT_TYPE_ID,
    DEPARTMENT_HR,
    DEPARTMENT_TCGV,
    HR_DEPARTMENT_IDS,
} from "@/core/config/teamLeadsCalendar/teamLeadsCalendarOption";
import {
    BOD,
    TEAMLEADER,
    TRUONG_BO_PHAN,
    TRUONG_NHOM,
} from "@/core/config/accountTypeOption";

export {
    TEAM_LEAD_ACCOUNT_TYPE_ID,
    DEPARTMENT_HR,
    DEPARTMENT_TCGV,
    HR_DEPARTMENT_IDS,
    BOD,
    TEAMLEADER,
    TRUONG_BO_PHAN,
    TRUONG_NHOM,
};

export const DEPARTMENT_TUYEN_HO = 44;

/**
 * Chỉ những nhân sự có account_type_id sau mới được tạo order tuyển dụng:
 * - BOD = 15
 * - Trưởng phòng = 16 (TEAMLEADER)
 * - Trưởng bộ phận = 32 (TRUONG_BO_PHAN)
 * - Trưởng nhóm = 34 (TRUONG_NHOM)
 */
export const ALLOWED_CREATE_ORDER_ACCOUNT_TYPE_IDS = [
    BOD,            // 15
    TEAMLEADER,     // 16
    TRUONG_BO_PHAN, // 32
    TRUONG_NHOM,    // 34
];

/**
 * Trích xuất tất cả các account_type_id của user từ currentUser và userProfile
 */
export function resolveAccountTypeIds(source, extra = null) {
    const ids = new Set();
    const push = (v) => {
        const n = Number(v);
        if (n) ids.add(n);
    };

    const extractOne = (u) => {
        if (!u) return;
        if (typeof u === "number" || typeof u === "string") {
            push(u);
            return;
        }
        if (typeof u !== "object") return;

        if (u.data && typeof u.data === "object") {
            extractOne(u.data);
        }
        if (u.user && typeof u.user === "object") {
            extractOne(u.user);
        }

        push(u.account_type_id);
        if (u.account_type) {
            if (typeof u.account_type === "object") {
                push(u.account_type.id);
                push(u.account_type.account_type_id);
            } else {
                push(u.account_type);
            }
        }
        if (u.profile && typeof u.profile === "object") {
            push(u.profile.account_type_id);
            if (u.profile.account_type) {
                if (typeof u.profile.account_type === "object") {
                    push(u.profile.account_type.id);
                    push(u.profile.account_type.account_type_id);
                } else {
                    push(u.profile.account_type);
                }
            }
        }
    };

    extractOne(source);
    extractOne(extra);

    // Fallback qua localStorage nếu chưa có
    if (ids.size === 0 && typeof window !== "undefined" && window.localStorage) {
        try {
            const rawUser = localStorage.getItem("user");
            if (rawUser) {
                const parsed = JSON.parse(rawUser);
                extractOne(parsed);
            }
        } catch (e) {}
    }

    return [...ids];
}

/**
 * Kiểm tra xem user có được quyền tạo order tuyển dụng không
 * Điều kiện:
 * 1. Thuộc nhóm account_type_id: BOD (15), Trưởng phòng (16), Trưởng bộ phận (32), Trưởng nhóm (34)
 * 2. HOẶC thuộc phòng Tuyển chọn Giáo viên (department_id = 52)
 */
export function canUserCreateOrder(user, extraProfile = null, extraDepartmentIds = []) {
    // 1. Kiểm tra account_type_id
    const accountTypeIds = resolveAccountTypeIds(user, extraProfile);
    if (ALLOWED_CREATE_ORDER_ACCOUNT_TYPE_IDS.some((allowedId) => accountTypeIds.includes(Number(allowedId)))) {
        return true;
    }

    // 2. Kiểm tra department_id = 44 (Phòng Tuyển hộ) hoặc department_id = 52
    const deptIds = new Set(resolveDepartmentIds(user));
    if (extraProfile) {
        resolveDepartmentIds(extraProfile).forEach((id) => deptIds.add(Number(id)));
    }
    (extraDepartmentIds || []).forEach((id) => {
        const n = Number(id);
        if (n) deptIds.add(n);
    });

    return deptIds.has(DEPARTMENT_TUYEN_HO) || deptIds.has(DEPARTMENT_TCGV);
}

/**
 * Kiểm tra xem user có quyền gán người duyệt CV không (department_id = 3 - Phòng Nhân sự / DEPARTMENT_HR)
 */
export function canUserAssignCvApprover(user, extraProfile = null, extraDepartmentIds = []) {
    const deptIds = new Set(resolveDepartmentIds(user));
    if (extraProfile) {
        resolveDepartmentIds(extraProfile).forEach((id) => deptIds.add(id));
    }
    (extraDepartmentIds || []).forEach((id) => {
        const n = Number(id);
        if (n) deptIds.add(n);
    });

    return deptIds.has(DEPARTMENT_HR);
}

export const ACCOUNT_TYPE_TEAMLEAD_ID = 16;

/**
 * Kiểm tra xem user có phải HR duyệt không:
 * account_type_id = 16 VÀ department_id = 3 (Phòng Nhân sự)
 */
export function canUserApproveHr(user, extraProfile = null, extraDepartmentIds = []) {
    const accountTypeIds = resolveAccountTypeIds(user, extraProfile);
    if (!accountTypeIds.includes(ACCOUNT_TYPE_TEAMLEAD_ID) && !accountTypeIds.includes(Number(TEAMLEADER))) {
        return false;
    }
    const deptIds = new Set(resolveDepartmentIds(user));
    if (extraProfile) {
        resolveDepartmentIds(extraProfile).forEach((id) => deptIds.add(Number(id)));
    }
    (extraDepartmentIds || []).forEach((id) => {
        const n = Number(id);
        if (n) deptIds.add(n);
    });
    return deptIds.has(DEPARTMENT_HR);
}

/**
 * Kiểm tra xem user có phải Teamlead (TCGV) duyệt không:
 * account_type_id = 16 VÀ department_id = 52 (Phòng Tuyển chọn Giáo viên)
 */
export function canUserApproveTeamlead(user, extraProfile = null, extraDepartmentIds = []) {
    const accountTypeIds = resolveAccountTypeIds(user, extraProfile);
    if (!accountTypeIds.includes(ACCOUNT_TYPE_TEAMLEAD_ID) && !accountTypeIds.includes(Number(TEAMLEADER))) {
        return false;
    }
    const deptIds = new Set(resolveDepartmentIds(user));
    if (extraProfile) {
        resolveDepartmentIds(extraProfile).forEach((id) => deptIds.add(Number(id)));
    }
    (extraDepartmentIds || []).forEach((id) => {
        const n = Number(id);
        if (n) deptIds.add(n);
    });
    return deptIds.has(DEPARTMENT_TUYEN_HO) || deptIds.has(DEPARTMENT_TCGV);
}

/**
 * Chỉ account_type_id = 16 của department_id = 3 và 52 mới được duyệt từ chối order
 */
export function canUserApproveOrRejectOrder(user, extraProfile = null, extraDepartmentIds = []) {
    return (
        canUserApproveHr(user, extraProfile, extraDepartmentIds) ||
        canUserApproveTeamlead(user, extraProfile, extraDepartmentIds)
    );
}

/**
 * Kiểm tra xem user có quyền gán HR phụ trách không:
 * Chỉ account_type_id = 16 của department_id = 3 (Phòng Nhân sự) mới được gán HR phụ trách
 */
export function canUserAssignPersonInCharge(user, extraProfile = null, extraDepartmentIds = []) {
    return canUserApproveHr(user, extraProfile, extraDepartmentIds);
}

/**
 * GIẢ ĐỊNH chung cho mọi nhân sự được hardcode bên dưới: con số là `currentUser.id`
 * (không có field "mã nhân viên" riêng trong currentUser hiện tại) - theo đúng tiền lệ
 * hardcode user cụ thể bằng id đã có sẵn tại `auth.module.js`
 * (`isActiveManager`/`isBOD` dùng `[295391, 266029].includes(state.user.id)`).
 * `matchesUser` bên dưới dò thêm cả `code`/`employee_code` nên khi backend trả thêm field
 * mã nhân viên riêng thì không phải sửa gì.
 */

/** Người điều phối - gán Teamlead phụ trách cho order Giáo viên (chị Cẩm Tú). */
export const COORDINATOR_USER_ID = 332991;
export const COORDINATOR_NAME = "Chị Cẩm Tú";

/** HR Lead - cấp duyệt bắt buộc của MỌI order (chị Ngà). */
export const HR_LEAD_USER_ID = 118990;
export const HR_LEAD_NAME = "Chị Ngà";

/** Chuyên trách tuyển dụng Giáo viên - cấp duyệt chỉ có ở order Giáo viên (chị Vân Anh). */
export const TEACHER_APPROVER_USER_ID = 320888;
export const TEACHER_APPROVER_NAME = "Chị Vân Anh";

/** So khớp 1 user với id cấu hình sẵn, dò qua mọi field định danh có thể có */
export function matchesUser(user, targetId) {
    if (!user || targetId == null) return false;
    const candidates = [
        user.id,
        user.code,
        user.employee_code,
        user.profile && user.profile.code,
        user.profile && user.profile.employee_code,
    ];
    return candidates.some((v) => v !== undefined && v !== null && String(v) === String(targetId));
}

export function isCoordinatorUser(user) {
    return matchesUser(user, COORDINATOR_USER_ID);
}

export function isHrLeadUser(user) {
    return matchesUser(user, HR_LEAD_USER_ID);
}

export function isTeacherApproverUser(user) {
    return matchesUser(user, TEACHER_APPROVER_USER_ID);
}

/** Dò department_id từ nhiều field khác nhau của currentUser/profile (giống team-leads-calendar) */
export function resolveDepartmentIds(source) {
    const ids = new Set();
    const push = (v) => {
        const n = Number(v);
        if (n) ids.add(n);
    };
    const ud = source && source.use_department;
    const digOne = (d) => {
        if (!d || typeof d !== "object") return;
        push(d.department_id);
        push(d.id);
        if (d.department) {
            push(d.department.id);
            push(d.department.department_id);
            if (d.department.department) push(d.department.department.id);
        }
    };
    if (Array.isArray(ud)) ud.forEach(digOne);
    else digOne(ud);
    push(source && source.profile && source.profile.department_id);
    // currentUser của một số API trả thẳng `department_id` / `department` ở cấp ngoài cùng
    push(source && source.department_id);
    digOne(source && source.department);
    return [...ids];
}

/* ------------------------------------------------------------------ *
 * Loại order — theo API backend JobRecruitmentRequest
 * ------------------------------------------------------------------ */
export const TYPE_TUYEN_CHINH_THUC = 1;
export const TYPE_TUYEN_HO = 2;

export const TYPE_RECRUITMENT = {
    [TYPE_TUYEN_CHINH_THUC]: "Tuyển chính thức",
    [TYPE_TUYEN_HO]: "Tuyển hộ (Giáo viên)",
};

export const ORDER_TYPE = {
    CHINH_THUC: TYPE_TUYEN_CHINH_THUC,
    TUYEN_HO: TYPE_TUYEN_HO,
    DEPARTMENT: "department",
    TEACHER: "teacher",
};

export const ORDER_TYPE_META = {
    [TYPE_TUYEN_CHINH_THUC]: {
        value: TYPE_TUYEN_CHINH_THUC,
        label: "Tuyển chính thức",
        icon: "el-icon-office-building",
        color: "#4f46e5",
        bg: "#eef2ff",
        approvalHint: `1 cấp duyệt — ${HR_LEAD_NAME} (HR Lead).`,
        assignHint: "Tuyển cho phòng ban trực thuộc.",
    },
    [TYPE_TUYEN_HO]: {
        value: TYPE_TUYEN_HO,
        label: "Tuyển hộ (Giáo viên)",
        icon: "el-icon-reading",
        color: "#b45309",
        bg: "#fef3c7",
        approvalHint: `2 cấp duyệt — Chuyên trách TCGV và ${HR_LEAD_NAME}.`,
        assignHint: "Tuyển cho khối giáo viên (TCGV).",
    },
    department: {
        value: TYPE_TUYEN_CHINH_THUC,
        label: "Tuyển chính thức",
        icon: "el-icon-office-building",
        color: "#4f46e5",
        bg: "#eef2ff",
        approvalHint: `1 cấp duyệt — ${HR_LEAD_NAME} (HR Lead).`,
        assignHint: "Tuyển cho phòng ban trực thuộc.",
    },
    teacher: {
        value: TYPE_TUYEN_HO,
        label: "Tuyển hộ (Giáo viên)",
        icon: "el-icon-reading",
        color: "#b45309",
        bg: "#fef3c7",
        approvalHint: `2 cấp duyệt — Chuyên trách TCGV và ${HR_LEAD_NAME}.`,
        assignHint: "Tuyển cho khối giáo viên (TCGV).",
    },
};

export const ORDER_TYPE_OPTIONS = [
    { value: TYPE_TUYEN_CHINH_THUC, label: TYPE_RECRUITMENT[TYPE_TUYEN_CHINH_THUC] },
    { value: TYPE_TUYEN_HO, label: TYPE_RECRUITMENT[TYPE_TUYEN_HO] },
];

/**
 * Cho phép tạo cả order "Tuyển chính thức" và "Tuyển hộ (Giáo viên)"
 */
export function canCreateTeacherOrder() {
    return true;
}

/** Các loại order được phép chọn trong form — hỗ trợ cả Tuyển chính thức và Tuyển hộ */
export function orderTypeOptionsFor() {
    return ORDER_TYPE_OPTIONS;
}

export function orderTypeOf(order) {
    if (!order) return TYPE_TUYEN_CHINH_THUC;
    const value = typeof order === "object" ? (order.type !== undefined ? order.type : order.order_type) : order;
    if (value === 2 || value === "2" || value === ORDER_TYPE.TEACHER) return TYPE_TUYEN_HO;
    return TYPE_TUYEN_CHINH_THUC;
}

export function orderTypeMetaOf(order) {
    const type = orderTypeOf(order);
    return ORDER_TYPE_META[type] || ORDER_TYPE_META[TYPE_TUYEN_CHINH_THUC];
}

/**
 * Loại order có phải "Tuyển hộ (Giáo viên)" không. Nhận cả object order/ứng viên
 * lẫn giá trị rời — số 1/2 của API `job-recruitment-requests` hoặc alias chuỗi
 * "department"/"teacher" còn sót trong dữ liệu cũ.
 */
export function isTeacherType(value) {
    const source = value && typeof value === "object" ? value : { type: value };
    return orderTypeOf(source) === TYPE_TUYEN_HO;
}

/** Order Giáo viên mới cần bước điều phối */
export function needsCoordination(order) {
    return isTeacherType(order);
}

/* ------------------------------------------------------------------ *
 * Cấp duyệt order
 * ------------------------------------------------------------------ */
export const APPROVAL_STAGE = {
    /** Chuyên trách tuyển dụng Giáo viên duyệt (chỉ có ở order Giáo viên) */
    TEACHER_SPECIALIST: "teacher_specialist",
    /** HR Lead duyệt (mọi order đều có) */
    HR_LEAD: "hr_lead",
};

export const APPROVAL_STAGE_META = {
    [APPROVAL_STAGE.TEACHER_SPECIALIST]: {
        label: "Chuyên trách TCGV",
        approver_id: TEACHER_APPROVER_USER_ID,
        approver_name: TEACHER_APPROVER_NAME,
        icon: "el-icon-reading",
    },
    [APPROVAL_STAGE.HR_LEAD]: {
        label: "HR Lead",
        approver_id: HR_LEAD_USER_ID,
        approver_name: HR_LEAD_NAME,
        icon: "el-icon-s-check",
    },
};

export const APPROVAL_STATUS = {
    PENDING: "pending",
    APPROVED: "approved",
    REJECTED: "rejected",
};

export const APPROVAL_STATUS_META = {
    [APPROVAL_STATUS.PENDING]: { label: "Chờ duyệt", type: "warning", color: "#d97706", icon: "el-icon-time" },
    [APPROVAL_STATUS.APPROVED]: { label: "Đã duyệt", type: "success", color: "#059669", icon: "el-icon-circle-check" },
    [APPROVAL_STATUS.REJECTED]: { label: "Từ chối", type: "danger", color: "#dc2626", icon: "el-icon-circle-close" },
};

/**
 * Các cấp duyệt của 1 order theo loại. Order Giáo viên có 2 cấp và duyệt SONG SONG
 * (không phải tuần tự) — cả 2 cùng nhận thông báo ngay khi order được tạo.
 */
export function approvalStagesOf(orderType) {
    return orderType === ORDER_TYPE.TEACHER
        ? [APPROVAL_STAGE.TEACHER_SPECIALIST, APPROVAL_STAGE.HR_LEAD]
        : [APPROVAL_STAGE.HR_LEAD];
}

/** Danh sách phiếu duyệt rỗng cho 1 order mới */
export function buildApprovals(orderType) {
    return approvalStagesOf(orderType).map((stage) => ({
        stage,
        approver_id: APPROVAL_STAGE_META[stage].approver_id,
        approver_name: APPROVAL_STAGE_META[stage].approver_name,
        status: APPROVAL_STATUS.PENDING,
        decided_at: null,
        note: "",
        reason: "",
    }));
}

/** Phiếu duyệt của order (tự dựng lại theo loại nếu BE chưa trả về) */
export function approvalsOf(order) {
    if (!order) return [];
    if (Array.isArray(order.approvals) && order.approvals.length) return order.approvals;
    return buildApprovals(orderTypeOf(order));
}

/** Phiếu duyệt mà `user` đang được quyền quyết trên order này; null nếu không có */
export function pendingApprovalFor(order, user) {
    if (!order || order.status !== ORDER_STATUS.PENDING_APPROVAL) return null;
    return (
        approvalsOf(order).find(
            (a) => a.status === APPROVAL_STATUS.PENDING && matchesUser(user, a.approver_id)
        ) || null
    );
}

/** `user` có phải người duyệt (bất kể đã quyết hay chưa) của order này không */
export function isApproverOf(order, user) {
    return approvalsOf(order).some((a) => matchesUser(user, a.approver_id));
}

/** {done, total, rejected} — dùng cho cột tiến độ duyệt ở bảng danh sách */
export function approvalProgress(order) {
    const list = approvalsOf(order);
    return {
        total: list.length,
        done: list.filter((a) => a.status === APPROVAL_STATUS.APPROVED).length,
        rejected: list.some((a) => a.status === APPROVAL_STATUS.REJECTED),
    };
}

/**
 * Trạng thái order sau khi một cấp vừa quyết xong:
 * - còn cấp chờ duyệt  -> vẫn PENDING_APPROVAL
 * - có cấp từ chối     -> REJECTED (order GV: 1 trong 2 từ chối là trượt)
 * - duyệt đủ + order GV                 -> PENDING_ASSIGNMENT (chờ chị Cẩm Tú điều phối)
 * - duyệt đủ + order phòng ban đã có TL -> FINDING_CV
 */
export function statusAfterApproval(order) {
    const list = approvalsOf(order);
    if (list.some((a) => a.status === APPROVAL_STATUS.REJECTED)) return ORDER_STATUS.REJECTED;
    if (list.some((a) => a.status === APPROVAL_STATUS.PENDING)) return ORDER_STATUS.PENDING_APPROVAL;
    if (needsCoordination(order) || !order.assigned_hr_teamlead_id) return ORDER_STATUS.PENDING_ASSIGNMENT;
    return ORDER_STATUS.FINDING_CV;
}

/* ------------------------------------------------------------------ *
 * Thông báo (notification) phát sinh trong luồng order
 * ------------------------------------------------------------------ */
export const NOTIFY_EVENT = {
    APPROVAL_REQUEST: "approval_request",
    APPROVAL_REMINDER: "approval_reminder",
    APPROVED: "approved",
    REJECTED: "rejected",
    ASSIGNED: "assigned",
};

export const NOTIFY_EVENT_META = {
    [NOTIFY_EVENT.APPROVAL_REQUEST]: { label: "Gửi duyệt order", icon: "el-icon-bell", color: "#0ea5e9" },
    [NOTIFY_EVENT.APPROVAL_REMINDER]: { label: "Nhắc duyệt order", icon: "el-icon-alarm-clock", color: "#d97706" },
    [NOTIFY_EVENT.APPROVED]: { label: "Order được duyệt", icon: "el-icon-circle-check", color: "#059669" },
    [NOTIFY_EVENT.REJECTED]: { label: "Order bị từ chối", icon: "el-icon-circle-close", color: "#dc2626" },
    [NOTIFY_EVENT.ASSIGNED]: { label: "Phân công Teamlead", icon: "el-icon-user", color: "#4f46e5" },
};

/* ------------------------------------------------------------------ *
 * Trạng thái order (JobRecruitmentRequest)
 * ------------------------------------------------------------------ */
export const STATUS_CHO_DUYET = 1;
export const STATUS_DA_DUYET = 2;
export const STATUS_DA_TU_CHOI = 3;
export const STATUS_DA_CO_NGUOI_PHU_TRACH = 4;
export const STATUS_DA_DANG_TUYEN = 5;
export const STATUS_SO_LOAI_CV = 6;
export const STATUS_TEAMLEAD_DA_DUYET_CV = 7;
export const STATUS_DANG_PHONG_VAN = 8;
export const STATUS_KET_THUC = 9;

export const STATUS_RECRUITMENT = {
    [STATUS_CHO_DUYET]: "Chờ duyệt",
    [STATUS_DA_DUYET]: "Đã duyệt",
    [STATUS_DA_TU_CHOI]: "Đã từ chối",
    [STATUS_DA_CO_NGUOI_PHU_TRACH]: "Đã có người phụ trách",
    [STATUS_DA_DANG_TUYEN]: "Đã đăng tuyển",
    [STATUS_SO_LOAI_CV]: "Sơ loại CV",
    [STATUS_TEAMLEAD_DA_DUYET_CV]: "Teamlead đã duyệt CV",
    [STATUS_DANG_PHONG_VAN]: "Đang phỏng vấn",
    [STATUS_KET_THUC]: "Kết thúc",
};

export const ORDER_STATUS = {
    CHO_DUYET: STATUS_CHO_DUYET,
    DA_DUYET: STATUS_DA_DUYET,
    DA_TU_CHOI: STATUS_DA_TU_CHOI,
    DA_CO_NGUOI_PHU_TRACH: STATUS_DA_CO_NGUOI_PHU_TRACH,
    DA_DANG_TUYEN: STATUS_DA_DANG_TUYEN,
    SO_LOAI_CV: STATUS_SO_LOAI_CV,
    TEAMLEAD_DA_DUYET_CV: STATUS_TEAMLEAD_DA_DUYET_CV,
    DANG_PHONG_VAN: STATUS_DANG_PHONG_VAN,
    KET_THUC: STATUS_KET_THUC,
    // Alias string
    PENDING_APPROVAL: "pending_approval",
    PENDING_ASSIGNMENT: "pending_assignment",
    FINDING_CV: "finding_cv",
    PENDING_REVIEW: "pending_review",
    APPROVED: "approved",
    REJECTED: "rejected",
    CANCELLED: "cancelled",
};

export const ORDER_STATUS_META = {
    [STATUS_CHO_DUYET]: { label: "Chờ duyệt", type: "warning", color: "#b45309", bg: "#fef3c7" },
    [STATUS_DA_DUYET]: { label: "Đã duyệt", type: "success", color: "#059669", bg: "#d1fae5" },
    [STATUS_DA_TU_CHOI]: { label: "Đã từ chối", type: "danger", color: "#dc2626", bg: "#fee2e2" },
    [STATUS_DA_CO_NGUOI_PHU_TRACH]: { label: "Đã có người phụ trách", type: "primary", color: "#2563eb", bg: "#dbeafe" },
    [STATUS_DA_DANG_TUYEN]: { label: "Đã đăng tuyển", type: "info", color: "#0284c7", bg: "#e0f2fe" },
    [STATUS_SO_LOAI_CV]: { label: "Sơ loại CV", type: "warning", color: "#d97706", bg: "#fef3c7" },
    [STATUS_TEAMLEAD_DA_DUYET_CV]: { label: "Teamlead đã duyệt CV", type: "success", color: "#10b981", bg: "#ecfdf5" },
    [STATUS_DANG_PHONG_VAN]: { label: "Đang phỏng vấn", type: "", color: "#6366f1", bg: "#eef2ff" },
    [STATUS_KET_THUC]: { label: "Kết thúc", type: "info", color: "#6b7280", bg: "#f3f4f6" },

    // String compatibility
    pending_approval: { label: "Chờ duyệt", type: "warning", color: "#b45309", bg: "#fef3c7" },
    pending_assignment: { label: "Chờ điều phối", type: "info", color: "#64748b", bg: "#f1f5f9" },
    finding_cv: { label: "Đang tìm CV", type: "warning", color: "#d97706", bg: "#fef3c7" },
    pending_review: { label: "Chờ duyệt CV", type: "", color: "#0ea5e9", bg: "#e0f2fe" },
    approved: { label: "Đã duyệt", type: "success", color: "#059669", bg: "#d1fae5" },
    rejected: { label: "Từ chối", type: "danger", color: "#dc2626", bg: "#fee2e2" },
    cancelled: { label: "Đã huỷ", type: "danger", color: "#dc2626", bg: "#fee2e2" },
};

export const ORDER_STATUS_OPTIONS = Object.entries(STATUS_RECRUITMENT).map(([value, label]) => ({
    value: Number(value),
    label,
}));

export const CHO_DUYET = 0;
export const DA_DUYET = 1;
export const TU_CHOI = 2;

export const TYPE_APPROVE = {
    [CHO_DUYET]: "Chờ duyệt",
    [DA_DUYET]: "Đã duyệt",
    [TU_CHOI]: "Từ chối",
};

export const APPROVE_STATUS_META = {
    [CHO_DUYET]: { label: "Chờ duyệt", type: "warning", color: "#d97706", icon: "el-icon-time" },
    [DA_DUYET]: { label: "Đã duyệt", type: "success", color: "#059669", icon: "el-icon-circle-check" },
    [TU_CHOI]: { label: "Từ chối", type: "danger", color: "#dc2626", icon: "el-icon-circle-close" },
};

/** Order còn đang chạy (chưa kết thúc bằng duyệt CV / từ chối / huỷ) */
export const ACTIVE_ORDER_STATUSES = [
    ORDER_STATUS.PENDING_APPROVAL,
    ORDER_STATUS.PENDING_ASSIGNMENT,
    ORDER_STATUS.FINDING_CV,
    ORDER_STATUS.PENDING_REVIEW,
];

/**
 * Kiểm tra xem yêu cầu tuyển dụng (order) có ở trạng thái ĐÃ DUYỆT hay không.
 * Điều kiện:
 * - KHÔNG PHẢI Từ chối (status !== 3, hr_approve !== 2, teamlead_approve !== 2)
 * - VÀ ĐÃ ĐƯỢC DUYỆT:
 *   + status >= 2 && status !== 3 (Đã duyệt, Đã có người phụ trách, Đã đăng tuyển...)
 *   + HOẶC cấp duyệt: Tuyển chính thức (hr_approve === 1); Tuyển hộ GV (hr_approve === 1 && teamlead_approve === 1)
 *
 * Yêu cầu chưa duyệt (Chờ duyệt) hoặc Từ chối: return false -> KHÔNG ĐƯỢC GÁN NGƯỜI PHỤ TRÁCH.
 */
export function isOrderApproved(order) {
    if (!order) return false;

    // 1. Kiểm tra trạng thái Từ chối -> Không được gán
    const statusNum = Number(order.status);
    if (statusNum === STATUS_DA_TU_CHOI) return false;
    if (Number(order.hr_approve) === TU_CHOI || Number(order.teamlead_approve) === TU_CHOI) return false;
    if (order.status === "rejected" || order.status === "cancelled") return false;

    // 2. Nếu status dạng số >= 2 (Đã duyệt, Đã có người phụ trách, Đã đăng tuyển...)
    if (statusNum >= STATUS_DA_DUYET && statusNum !== STATUS_DA_TU_CHOI) {
        return true;
    }

    // 3. Kiểm tra theo cấp duyệt
    const isTeacher = isTeacherType(order);
    if (isTeacher) {
        return Number(order.hr_approve) === DA_DUYET && Number(order.teamlead_approve) === DA_DUYET;
    }

    return Number(order.hr_approve) === DA_DUYET;
}

/**
 * Kiểm tra xem yêu cầu tuyển dụng (order) có ở trạng thái TỪ CHỐI hay không.
 */
export function isOrderRejected(order) {
    if (!order) return false;
    const statusNum = Number(order.status);
    if (statusNum === STATUS_DA_TU_CHOI) return true;
    if (Number(order.hr_approve) === TU_CHOI || Number(order.teamlead_approve) === TU_CHOI) return true;
    if (order.status === "rejected" || order.status === "da_tu_choi" || order.status === "cancelled" || order.status === ORDER_STATUS.REJECTED) return true;
    return false;
}

/* ------------------------------------------------------------------ *
 * Đọc field của 1 order
 * ------------------------------------------------------------------ *
 * API `job-recruitment-requests` trả `title`, `department: {id, name}`,
 * `person_in_charge_id` / `person_in_charge: {id, name}` và `status` dạng số.
 * Các hàm dưới đây đọc được cả shape đó lẫn shape cũ (`position_title`,
 * `department_name`, `assigned_hr_teamlead_*`, status dạng chuỗi) nên mọi màn
 * hình dùng chung một cách lấy dữ liệu, không phụ thuộc nguồn.
 */

/** Trạng thái order dạng số nếu BE trả số (kể cả khi trả về dưới dạng chuỗi số) */
export function orderStatusOf(order) {
    const status = order && order.status;
    if (typeof status === "string" && /^\d+$/.test(status)) return Number(status);
    return status;
}

export function orderTitleOf(order) {
    if (!order) return "";
    return order.title || order.position_title || "";
}

export function orderDepartmentNameOf(order) {
    if (!order) return "";
    return (order.department && order.department.name) || order.department_name || "";
}

/** Teamlead phụ trách order -> {id, name}; id null nghĩa là chưa phân công */
export function orderPersonInChargeOf(order) {
    if (!order) return { id: null, name: "" };
    const id =
        order.person_in_charge_id ||
        (order.person_in_charge && order.person_in_charge.id) ||
        order.assigned_hr_teamlead_id ||
        null;
    const name =
        (order.person_in_charge && order.person_in_charge.name) || order.assigned_hr_teamlead_name || "";
    return { id: id || null, name: name || "" };
}

/** Trạng thái 1 CV trong lịch sử đề xuất của order */
export const CANDIDATE_STATUS = {
    PENDING: "pending",
    APPROVED: "approved",
    REJECTED: "rejected",
};

export const CANDIDATE_STATUS_META = {
    [CANDIDATE_STATUS.PENDING]: { label: "Chờ duyệt", type: "warning", color: "#d97706" },
    [CANDIDATE_STATUS.APPROVED]: { label: "Đã duyệt", type: "success", color: "#059669" },
    [CANDIDATE_STATUS.REJECTED]: { label: "Đã từ chối", type: "danger", color: "#dc2626" },
};

export const PRIORITY = {
    NORMAL: "normal",
    URGENT: "urgent",
};

export const PRIORITY_META = {
    [PRIORITY.NORMAL]: { label: "Bình thường", type: "" },
    [PRIORITY.URGENT]: { label: "Gấp", type: "danger" },
};

export const PRIORITY_OPTIONS = Object.values(PRIORITY).map((value) => ({
    value,
    label: PRIORITY_META[value].label,
}));

/* ------------------------------------------------------------------ *
 * Khu vực tuyển dụng
 * ------------------------------------------------------------------ */
export const AREA_MIEN_BAC = 1;
export const AREA_MIEN_TRUNG = 2;
export const AREA_MIEN_NAM = 3;
export const AREA_ONLINE = 4;

export const RECRUITMENT_AREA = {
    [AREA_MIEN_BAC]: "Miền Bắc",
    [AREA_MIEN_TRUNG]: "Miền Trung",
    [AREA_MIEN_NAM]: "Miền Nam",
    [AREA_ONLINE]: "Online",
};

export const RECRUITMENT_AREA_OPTIONS = [
    { value: AREA_MIEN_BAC, label: "Miền Bắc" },
    { value: AREA_MIEN_TRUNG, label: "Miền Trung" },
    { value: AREA_MIEN_NAM, label: "Miền Nam" },
    { value: AREA_ONLINE, label: "Online" },
];

export function getRecruitmentAreaLabel(value) {
    if (value === null || value === undefined || value === "") return "";
    return RECRUITMENT_AREA[Number(value)] || String(value);
}

/* ------------------------------------------------------------------ *
 * Mẫu Order tuyển dụng
 * ------------------------------------------------------------------ *
 * Người tạo order chọn 1 mẫu để tự điền sẵn vị trí / mô tả / yêu cầu, sau đó
 * chỉnh lại cho đúng nhu cầu. Mẫu "Tự nhập" giữ form trống như trước.
 */
export const ORDER_TEMPLATE_BLANK = "blank";

export const ORDER_TEMPLATES = [
    {
        id: ORDER_TEMPLATE_BLANK,
        label: "Tự nhập (không dùng mẫu)",
        order_type: null,
        position_title: "",
        quantity: 1,
        description: "",
        requirements: "",
    },
    {
        id: "tpl-sale",
        label: "Chuyên viên tư vấn tuyển sinh",
        order_type: ORDER_TYPE.DEPARTMENT,
        position_title: "Chuyên viên tư vấn tuyển sinh",
        quantity: 2,
        description:
            "Tư vấn khoá học cho học viên và phụ huynh qua data trung tâm cấp; theo dõi, chăm sóc học viên tới khi nhập học.",
        requirements:
            "- Tốt nghiệp Cao đẳng trở lên\n- Giao tiếp tốt, chịu được áp lực doanh số\n- Ưu tiên có kinh nghiệm sale giáo dục",
    },
    {
        id: "tpl-cskh",
        label: "Nhân viên chăm sóc khách hàng",
        order_type: ORDER_TYPE.DEPARTMENT,
        position_title: "Nhân viên chăm sóc khách hàng",
        quantity: 1,
        description:
            "Chăm sóc học viên trong quá trình học, xử lý phản hồi, nhắc lịch học và thu thập đánh giá chất lượng.",
        requirements:
            "- Giọng nói dễ nghe, kỹ năng lắng nghe tốt\n- Thành thạo tin học văn phòng\n- Ưu tiên có kinh nghiệm CSKH",
    },
    {
        id: "tpl-office",
        label: "Nhân sự khối văn phòng",
        order_type: ORDER_TYPE.DEPARTMENT,
        position_title: "",
        quantity: 1,
        description: "Bổ sung nhân sự khối văn phòng theo định biên đã duyệt của phòng ban.",
        requirements:
            "- Tốt nghiệp đúng chuyên ngành\n- Tối thiểu 1 năm kinh nghiệm ở vị trí tương đương\n- Kỹ năng làm việc nhóm, báo cáo",
    },
    {
        id: "tpl-gv-tienganh",
        label: "Giáo viên tiếng Anh",
        order_type: ORDER_TYPE.TEACHER,
        position_title: "Giáo viên tiếng Anh",
        quantity: 1,
        description:
            "Đứng lớp theo giáo án và sản phẩm của trung tâm; tham gia đủ 4 vòng đào tạo (Elearning, gia nhập & sản phẩm, L.I.B, coaching) trước khi nhận lớp.",
        requirements:
            "- IELTS 7.0+ hoặc tương đương\n- Ưu tiên có chứng chỉ sư phạm\n- Cam kết lịch dạy theo khung giờ trung tâm xếp",
    },
    {
        id: "tpl-gv-tro-giang",
        label: "Trợ giảng",
        order_type: ORDER_TYPE.TEACHER,
        position_title: "Trợ giảng",
        quantity: 2,
        description:
            "Hỗ trợ giáo viên chính trên lớp, theo dõi bài tập và tiến độ học viên, phối hợp với bộ phận vận hành lớp.",
        requirements:
            "- Sinh viên năm 3 trở lên hoặc đã tốt nghiệp\n- Phát âm chuẩn, nhiệt tình\n- Sắp xếp được lịch tối và cuối tuần",
    },
];

export function findOrderTemplate(id) {
    return ORDER_TEMPLATES.find((t) => t.id === id) || null;
}
